import { IconButton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { ViewPage } from "../../../../../../../assets/icons";
import { formatNumber } from "../../../../../../../common/utils";
import {
	MeasurableValueDTO,
	TripsPageDTO,
	TripsPageItemDTO,
} from "../../../../../../../datasources/api-client/data-contracts";
import { headerCellMeasure } from "../../../../../utils";

export const getRows = (data: TripsPageDTO) => {
	if (!data) return [];

	return data.items.map((trip: TripsPageItemDTO) => ({
		id: trip.id,
		date: trip.departureAt,
		description: trip.description,
		origin: trip.origin.description,
		destination: trip.destination.description,
		distance: trip.distance,
		temperature: trip.temperatureDescription,
		weight: trip.weight,
		dedicated: trip.isDedicatedDescription,
		tCo2eWtw: trip.tCo2eWtw,
		tCo2eTtw: trip.tCo2eTtw,
	}));
};

export const colsData = (): GridColDef[] => [
	{
		field: "id",
		headerName: "ID",
		hideable: true,
		flex: 0.7,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ?? "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "date",
		headerName: "date",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "description",
		headerName: "description",
		hideable: true,
		sortable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value || "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "origin",
		headerName: "departure",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ?? "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "destination",
		headerName: "arrival",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ?? "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "distance",
		headerName: "distance",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},

	{
		field: "temperature",
		headerName: "temperature",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value || "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "weight",
		headerName: "weight",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},

	{
		field: "dedicated",
		headerName: "type of transport",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value || "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "tCo2eWtw",
		headerName: "emissions",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},

	{
		field: "tCo2eTtw",
		headerName: "emissions",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},
];

export const getColumns = (
	headerTextColor: string,
	t: TFunction,
	onNavigate: (id: string, code?: string) => void,
	dataSample?: Record<string, any>
): GridColDef[] => {
	const actionCol: GridColDef = {
		field: "actions",
		headerName: t("common:actions"),
		hideable: true,
		sortable: false,
		width: 100,
		align: "center",
		renderHeader: () => (
			<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="body2">
				{t("common:actions")}
			</Typography>
		),
		renderCell: (params) => {
			const id = params.row.id;
			const code = params.row.id;

			return (
				<IconButton
					onClick={(e) => {
						e.stopPropagation();
						onNavigate(id, code);
					}}
				>
					<ViewPage fontSize="small" />
				</IconButton>
			);
		},
	};

	const baseCols = colsData().map((col) => {
		const label = t(`tables header/${col.headerName}`);

		return {
			...col,
			headerName: label,

			renderHeader: () => (
				<Stack direction="row" spacing={1} alignItems="baseline">
					<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="body2">
						{label}
					</Typography>

					<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="caption">
						{headerCellMeasure(col, dataSample || {})}
					</Typography>
				</Stack>
			),
		};
	});

	return [...baseCols, actionCol];
};
